import { makeBIMDataApiClient } from "@bimdata/typescript-fetch-api-client";
import { BasePlugin } from "@uppy/core";
import dayjs from "dayjs";
import { useDocumentStore } from "@/store/modules/document.js";
import { globalEnv } from "@/constants";

async function detectMimeType(file) {
  if (file.type) {
    return file.type;
  }
  const buffer = await file.slice(0, 8).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  if (hex.startsWith("89504e47")) return "image/png";
  if (hex.startsWith("ffd8ff")) return "image/jpeg";
  if (hex.startsWith("25504446")) return "application/pdf";
  if (hex.startsWith("47494638")) return "image/gif";
  if (hex.startsWith("504b0304")) return "application/zip";

  return "application/octet-stream"; // fallback
}

const apiUrl = globalEnv.bimdataApiBaseUrl;
let collaborationApi;

const initBimdataAuth = (accessToken) => {
  collaborationApi = makeBIMDataApiClient({
    apiUrl,
    accessToken,
  }).collaborationApi;
};

const UPLOAD_TIMEOUT_MS = 120_000;

const uploadFile = async (projectOriginId, file) => {
  if (!collaborationApi) {
    throw new Error("BIMData API client is not initialized");
  }

  if (!projectOriginId) {
    throw new Error("Project origin ID is required for file upload");
  }

  if (!file) {
    throw new Error("A file is required for file upload");
  }

  if (!file.size) {
    throw new Error("Cannot upload an empty file");
  }
  const fileType = await detectMimeType(file);

  const formData = new FormData();
  formData.append("name", file.name);
  formData.append("file", file);

  const cloudId = import.meta.env.VITE_APP_CLOUD_ID;

  try {
    const response = await fetch(`${apiUrl}/cloud/${cloudId}/project/${projectOriginId}/document`, {
      method: "POST",
      headers: collaborationApi.configuration.headers,
      body: formData,
      signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      throw new Error(`BIMData upload failed (${response.status}) ${detail}`.trim());
    }

    const { id } = await response.json();
    return {
      id,
      mimeType: fileType,
      size: file.size,
      fileName: file.name,
    };
  } catch (e) {
    console.error("Error uploading file to BIMData:", e);
    throw e;
  }
};

export { initBimdataAuth, collaborationApi, uploadFile };

export class BimdataUploader extends BasePlugin {
  constructor(uppy, opts) {
    super(uppy, opts);
    this.type = "uploader";
    this.id = opts.id || "BimdataUploader";
    this.title = "Bimdata Uploader";
    this.documentStore = useDocumentStore();

    this.uploadFiles = this.uploadFiles.bind(this);
  }

  async uploadFile(file) {
    try {
      this.uppy.setFileState(file.id, {
        progress: { uploadStarted: Date.now(), percentage: 0 },
        status: "uploading",
      });

      const document = await this.documentStore.createDocument({
        file: file.data,
        form: {
          name: file.name,
          datetime: dayjs().format("YYYY-MM-DD"),
        },
      });

      this.uppy.setFileState(file.id, {
        progress: { percentage: 100 },
        status: "uploaded",
      });

      this.uppy.emit("upload-success", file, { body: document });
    } catch (error) {
      this.uppy.setFileState(file.id, { status: "error" });
      this.uppy.emit("upload-error", file, error);
    }
  }

  uploadFiles() {
    const files = this.uppy.getFiles();
    return Promise.all(files.map((file) => this.uploadFile(file)));
  }

  install() {
    this.uppy.addUploader(this.uploadFiles);
  }

  uninstall() {
    this.uppy.removeUploader(this.uploadFiles);
  }
}
